import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from '../api/types';
import { apiClient, tokenStorage } from '../api/client';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, secret: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    // 1. Cookie-first restoration: call GET /api/me with credentials: 'include' without Bearer header
    try {
      const profile = await apiClient.me.getProfile({ skipToken: true });
      setUser(profile);
      setIsLoading(false);
      return;
    } catch {
      // Cookie authentication was absent or failed
    }

    // 2. If cookie failed and sessionStorage token exists: fallback to Bearer
    const token = tokenStorage.get();
    if (token) {
      try {
        const profile = await apiClient.me.getProfile({ token });
        setUser(profile);
        setIsLoading(false);
        return;
      } catch {
        // Invalid/expired Bearer token
        tokenStorage.clear();
      }
    }

    // 3. Otherwise unauthenticated
    setUser(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (identifier: string, secret: string): Promise<void> => {
    setIsLoading(true);
    try {
      await apiClient.auth.login(identifier, secret);
      const profile = await apiClient.me.getProfile();
      setUser(profile);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    await apiClient.auth.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
